import { isNil } from 'lodash'

// =============================================================================
// Pure DOM-selection → character-offset helpers shared by the comment surfaces
// (prompt segments + call-transcript turns). Offsets are indices into a base
// string the caller measures against; the caller validates that the string and
// the rendered text line up (the BE re-checks the snippet for transcript
// comments, so a mismatch is rejected rather than silently mis-anchored).
// =============================================================================

// The active selection Range iff it is non-empty and both endpoints live inside
// `container`; otherwise null. Used to decide whether to show the popover.
export const getSelectionRangeWithin = (container) => {
  if (isNil(container)) {
    return null
  }
  const selection = window.getSelection()
  if (isNil(selection) || selection.rangeCount === 0) {
    return null
  }
  const range = selection.getRangeAt(0)
  if (range.collapsed) {
    return null
  }
  if (!container.contains(range.startContainer) || !container.contains(range.endContainer)) {
    return null
  }
  return range
}

// Character offset of a (node, offsetInNode) point within `root`, counting only
// text-node content in document order. Assumes the concatenation of root's text
// nodes equals the string the caller measures offsets against.
export const textOffsetWithin = (root, node, offsetInNode) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let total = 0
  let current = walker.nextNode()
  while (!isNil(current)) {
    if (current === node) {
      return total + offsetInNode
    }
    total += current.textContent.length
    current = walker.nextNode()
  }
  return total
}

// Default range → {start,end} resolver: offsets index into the concatenation of
// `container`'s text nodes. Correct when the rendered text equals the base
// string (a transcript turn). Surfaces whose DOM diverges from the base string
// (the segmented prompt, with collapsed <system> blocks) pass their own
// resolver to SelectionCommentPopover instead.
export const rangeToOffsets = (container, range) => {
  const first = textOffsetWithin(container, range.startContainer, range.startOffset)
  const second = textOffsetWithin(container, range.endContainer, range.endOffset)
  return first <= second ? { start: first, end: second } : { start: second, end: first }
}
